/* eslint-disable @typescript-eslint/no-explicit-any */
import { FilterOptionsState } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import { NotificationSortBy } from 'src/pages/NotificationTask/enum/NotificationTaskEnum'
import Fuse from 'fuse.js'
import moment from 'moment'
import _ from 'lodash'
import axios from 'axios'
import {
  EventInfo,
  EventLocation,
  EventOrder,
  IAirtableDesignerInfo,
  ILocation,
  PortalUserModel,
  ProductImage,
  RecipeHardGood,
  ReducerProduct,
  Stem,
  StemOrderUnit,
  SupplierStemOrderUnit
} from './common.interface'
import { ProductImageType, ProposalUnitType } from './enum'

const heicExtRegex = /(\.(heic|heif)$|(image\/heic))/i

export const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  window.matchMedia('only screen and (max-width: 700px)').matches

export interface SummaryRecipeHardGood extends RecipeHardGood {
  totalHardGood: number
  totalPrice: number
  orderHardGoodSupplier?: string
}

export const formatCurrency = (val: string | number | undefined) => {
  let num = val
  if (typeof val === 'string') {
    num = parseFloat(val)
  }
  if (!val) {
    num = 0
  }
  return Intl.NumberFormat('en-IN', { style: 'currency', currency: 'USD' }).format(num as number)
}

export const formatPrice = (val: number | undefined) => {
  if (val === undefined) {
    return ''
  }
  const price = val / 100
  return formatCurrency(price)
}

export const getBunchesNeeded = (stemCount: string | number, bunchSize?: string) => {
  const bunchS = parseInt(bunchSize || '1')
  let stemC = stemCount
  if (typeof stemC === 'string') {
    stemC = parseInt(stemC)
  }
  return Math.ceil(stemC / bunchS)
}

export const getTotalEstimate = (stem: SupplierStemOrderUnit | StemOrderUnit | Stem, bunchesNeeded: number) => {
  let unitPrice
  if (stem.bunchSize) {
    unitPrice = parseFloat(stem.pricePerBunch || '0')
  } else {
    unitPrice = parseFloat(stem.pricePerStem || '0')
  }
  return `$${(bunchesNeeded * unitPrice).toFixed(2)}`
}

export const getUnitPrice = (stem: SupplierStemOrderUnit | StemOrderUnit | Stem) => {
  let unitPrice
  if (stem.bunchSize) {
    unitPrice = parseFloat(stem.pricePerBunch || '0')
  } else {
    unitPrice = parseFloat(stem.pricePerStem || '0')
  }
  return +unitPrice
}

export const onlyUnique = <T>(data: T[]): T[] => {
  return data.filter((value, index, self) => {
    return self.indexOf(value) === index
  })
}

export const capitalizeTheFirstLetterOfEachWord = (words: string) => {
  return words.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
}

export const formatMonths = (months: string[] | undefined) => {
  if (!months || months.length < 2) {
    return months
  }
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  let expectedIndex = monthNames.indexOf(months[0]) + 1
  for (let i = 1; i < months.length; i++) {
    if (monthNames.indexOf(months[i]) !== expectedIndex) {
      return months
    }
    expectedIndex++
  }

  return `${months[0]} - ${months[months.length - 1]}`
}

export function fuzzySearchMultipleWords<ItemType = string>(
  rows: ItemType[], // array of data [{a: "a", b: "b"}, {a: "c", b: "d"}]
  keys: string[], // keys to search ["a", "b"]
  filterValue: string // potentially multi-word search string "two words"
): Array<ItemType> {
  if (!filterValue || !filterValue.length) {
    return rows
  }

  const terms = filterValue.split(' ')
  if (!terms) {
    return rows
  }

  // reduceRight will mean sorting is done by score for the _first_ entered word.
  return terms.reduceRight((results, term) => matchSorter(results, term, { keys }), rows)
}

export const filterOptions = <T = any>(options: T[], state: FilterOptionsState<T>) => {
  return fuzzySearchMultipleWords(options, ['name'], state.inputValue)
}

export const filterOptionsFilteredDropdown = <T = any>(options: T[], state: FilterOptionsState<T>) => {
  return fuzzySearchMultipleWords(options, ['label'], state.inputValue)
}

export const filterOptionsStem = <T = any>(rows: T[], state: FilterOptionsState<T>) => {
  const configOptionStem = {
    includeScore: true,
    useExtendedSearch: true,
    keys: ['name', 'pricePerStem']
  }
  const filterValue = state.inputValue
  if (!filterValue || !filterValue.length) {
    return rows
  }

  const terms = filterValue.split(' ')
  if (!terms) {
    return rows
  }
  const fuse = new Fuse(rows, configOptionStem)
  return fuse
    .search(
      state.inputValue
        .split(' ')
        ?.filter((i) => i)
        ?.map((is) => `'${is}`)
        .join(' | ')
    )
    .map((i) => i.item)
}

export const copyElementToClipboard = (element: string) => {
  try {
    window.getSelection()?.removeAllRanges()
    const range = document.createRange()
    range.selectNode(document.getElementById(element) as HTMLElement)
    window.getSelection()?.addRange(range)
    document.execCommand('copy')
    window.getSelection()?.removeAllRanges()
    return true
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('error', error)
    return false
  }
}
export interface Params {
  [key: string]: any
}

export const ObjectToFormData = (params: Params = {}) => {
  const formData = new FormData()
  Object.keys(params).forEach((key) => {
    formData.append(key, params[key])
  })
  return formData
}

export const formatDateToMDY = (date?: string) => {
  if (!date) {
    return
  }

  return new Date(date).toLocaleDateString('en-US')
}

export const formattedDateToMMDDYYYY = (date?: string) => {
  if (!date) {
    return
  }

  return moment(date).format('MM/DD/YYYY')
}

export const getProductDefaultImageUrl = (images: ProductImage[]) => {
  if (!images || (images && !images.length)) return ''
  const defaultImage = images.find((image) => image.type === ProductImageType.DEFAULT)
  if (defaultImage) return defaultImage.imageUrl
  return ''
}
export const getDefaultProductImageEventId = (images: ProductImage[]) => {
  if (!images || (images && !images.length)) return ''
  const defaultImage = images.find((image) => image.type === ProductImageType.DEFAULT)
  if (defaultImage) return defaultImage.eventId
  return ''
}

export const getSummaryHardGoodsFromProduct = (products: ReducerProduct[]): SummaryRecipeHardGood[] => {
  const summaryRecipeHardGoodsTemp: SummaryRecipeHardGood[] = []
  if (products && products.length) {
    products.map((p) => {
      p.recipeHardGoods?.map((rhg) => {
        const ind = summaryRecipeHardGoodsTemp.findIndex((srhg) => srhg.hardGoodId === rhg.hardGoodId)
        if (ind > -1) {
          summaryRecipeHardGoodsTemp[ind] = {
            ...rhg,
            quantity: summaryRecipeHardGoodsTemp[ind].quantity + rhg.quantity,
            totalHardGood: summaryRecipeHardGoodsTemp[ind].totalHardGood + p.quantity * rhg.quantity,
            totalPrice:
              summaryRecipeHardGoodsTemp[ind].totalPrice + p.quantity * rhg.quantity * +(rhg.hardGood?.price || 0)
          }
        } else {
          summaryRecipeHardGoodsTemp.push({
            ...rhg,
            totalHardGood: p.quantity * rhg.quantity,
            totalPrice: p.quantity * rhg.quantity * +(rhg.hardGood?.price || 0)
          })
        }
        return rhg
      })
      return p
    })
  }
  return summaryRecipeHardGoodsTemp
}

export const nl2br = (str: string) => {
  if (str) {
    return str.replace(/(?:\r\n|\r|\n)/g, '<br>')
  }
  return ''
}

export const rmnl = (str: string) => {
  if (str) {
    return str.replace(/(?:\r\n|\r|\n)/g, '')
  }
  return ''
}

export const capitalizeFirstLetter = (str: string) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const keyTypeToHumanReadable = (keyType: string) => {
  if (!keyType) return ''
  return keyType
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

export const roundTo = (value?: number, fractionDigits?: number | undefined): number | undefined => {
  if (!value) return value
  const fraction = 10 ** (fractionDigits || 0)
  return Math.round(value * fraction) / fraction
}

export const makeId = (length: number) => {
  let result = ''
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const charactersLength = characters.length
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }
  return result
}

export const roundTotalToNext15 = (totalMinutes: number, returnNumber?: boolean) => {
  let h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  let roundM = m
  if (m <= 15) {
    roundM = 15
  } else if (m <= 30) {
    roundM = 30
  } else if (m <= 45) {
    roundM = 45
  } else {
    roundM = 0
    h += 1
  }
  if (returnNumber) {
    return h * 60 + roundM
  }
  return `${h} ${h > 1 ? 'hrs' : 'hr'} ${roundM} ${roundM > 1 ? 'mins' : 'min'}`
}

export const round5 = (aNumber: number) => {
  return Math.ceil(aNumber / 5) * 5
}

export const roundTotalToNext5 = (totalMinutes: number, returnNumber?: boolean) => {
  let h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  let roundM = round5(m)
  if (roundM >= 60) {
    roundM = 0
    h += 1
  }
  if (returnNumber) {
    return h * 60 + roundM
  }
  return `${h} ${h > 1 ? 'hrs' : 'hr'} ${roundM} ${roundM > 1 ? 'mins' : 'min'}`
}

export const round = (value: number, precision: number) => {
  // eslint-disable-next-line no-restricted-properties
  const multiplier = Math.pow(10, precision || 0)
  return Math.round(value * multiplier) / multiplier
}

export const isHeicType = (url: string) => {
  return url.match(heicExtRegex) !== null
}

export const getAlphabetCharacter = (index?: number | null, isUpperCase = false) => {
  if (typeof index !== 'number') {
    return ''
  }
  const baseCode = isUpperCase ? 65 : 97 // 65 for 'A', 97 for 'a'
  return String.fromCharCode(baseCode + index - 1)
}

export const getAllEventServicesInText = (allLocations: EventLocation[]) => {
  return allLocations.map((l) => l.eventServices?.map((es) => keyTypeToHumanReadable(es.type || ''))).join(', ')
}

export const getLocationDetailFromAddress = async (address: string) => {
  const geocoding = await axios.get(
    `https://maps.google.com/maps/api/geocode/json?key=AIzaSyB2XEVN9zLATh1CAZV40hk-JYqRdvxN7Q0&address=${encodeURIComponent(
      address
    )}`
  )
  let streetNumber = ''
  let route = ''
  let address_2 = ''
  let city = ''
  let state = ''
  let zipCode = ''
  geocoding?.data?.results[0]?.address_components?.forEach((component: any) => {
    const componentType = component.types[0]
    // eslint-disable-next-line default-case
    switch (componentType) {
      case 'street_number': {
        streetNumber = component.long_name
        break
      }
      case 'route': {
        route = component.long_name
        break
      }
      case 'subpremise': {
        address_2 = component.long_name
        break
      }
      case 'postal_code': {
        zipCode = component.long_name
        break
      }
      case 'locality':
        city = component.long_name
        break
      case 'administrative_area_level_1': {
        state = component.short_name
        break
      }
      case 'country':
        break
      default:
        break
    }
  })
  return {
    city,
    state,
    zipCode,
    streetAddress: `${streetNumber} ${route}`,
    place: '',
    address_2
  }
}

export const locationToFullAddress = (location: EventLocation) => {
  if (!location) return ''
  const address = _.compact([
    location?.streetAddress?.trim(),
    location?.city?.trim(),
    location?.state?.trim(),
    location?.country?.trim()
  ]).join(', ')

  return `${address} ${location?.zipCode || ''}`.trim()
}
export const getDeliveryAddress = async (eventOrderInput: EventOrder, eventInfoInput?: EventInfo) => {
  const location = {} as ILocation
  let deliveryAddress = ''
  const designerPortalUser = eventInfoInput?.designerPortalUser || []
  const designerBooked = eventInfoInput?.airtableInfo?.designerBooked || []
  switch (true) {
    case !!eventOrderInput?.address_1:
      deliveryAddress = `${eventOrderInput?.address_1 || ''} ${eventOrderInput?.address_2 || ''}, ${
        eventOrderInput?.city || ''
      }, ${eventOrderInput?.state || ''} ${eventOrderInput?.zip || ''}`
      location.name = eventOrderInput?.recipient
      location.streetAddress = eventOrderInput?.address_1
      location.address_2 = eventOrderInput?.address_2
      location.city = eventOrderInput?.city
      location.state = eventOrderInput?.state
      location.zipCode = eventOrderInput?.zip
      location.fullAddress = rmnl(deliveryAddress)
      location.addressSource = 'eventOrder'
      break

    case !!designerPortalUser[0]?.deliveryAddress_1:
      deliveryAddress = `${designerPortalUser[0]?.deliveryAddress_1 || ''} ${
        designerPortalUser[0]?.deliveryAddress_2 || ''
      }, ${designerPortalUser[0]?.deliveryCity || ''}, ${designerPortalUser[0]?.deliveryState || ''} ${
        designerPortalUser[0]?.deliveryZip || ''
      }`
      location.name = `${designerPortalUser[0]?.firstName || ''} ${designerPortalUser[0]?.lastName || ''}`
      location.streetAddress = designerPortalUser[0]?.deliveryAddress_1 || ''
      location.address_2 = designerPortalUser[0]?.deliveryAddress_2 || ''
      location.city = designerPortalUser[0]?.deliveryCity || ''
      location.state = designerPortalUser[0]?.deliveryState || ''
      location.zipCode = designerPortalUser[0]?.deliveryZip || ''
      location.fullAddress = rmnl(deliveryAddress)
      location.addressSource = 'DesignerPortalUser'
      break

    case !!designerBooked[0]?.addressDelivery:
      {
        deliveryAddress = `${designerBooked[0]?.addressDelivery}`
        const parsedLocation = await getLocationDetailFromAddress(deliveryAddress)
        location.name = `${designerBooked[0]?.name || ''}`
        location.streetAddress = parsedLocation?.streetAddress || ''
        location.address_2 = parsedLocation?.address_2 || ''
        location.city = parsedLocation?.city || ''
        location.state = parsedLocation?.state || ''
        location.zipCode = parsedLocation?.zipCode || ''
        location.fullAddress = rmnl(deliveryAddress)
        location.addressSource = 'DesignerBookedOnAirtable'
      }
      break

    case !!designerPortalUser[0]?.address_1:
      deliveryAddress = `${designerPortalUser[0]?.address_1 || ''} ${designerPortalUser[0]?.address_2 || ''}, ${
        designerPortalUser[0]?.city || ''
      }, ${designerPortalUser[0]?.state || ''} ${designerPortalUser[0]?.zip || ''}`
      location.name = `${designerPortalUser[0]?.firstName || ''} ${designerPortalUser[0]?.lastName || ''}`
      location.streetAddress = designerPortalUser[0]?.address_1 || ''
      location.address_2 = designerPortalUser[0]?.address_2 || ''
      location.city = designerPortalUser[0]?.city || ''
      location.state = designerPortalUser[0]?.state || ''
      location.zipCode = designerPortalUser[0]?.zip || ''
      location.fullAddress = rmnl(deliveryAddress)
      location.addressSource = 'DesignerPortalUser'
      break

    case !!designerBooked[0]?.addressHome:
      {
        deliveryAddress = `${designerBooked[0]?.addressHome || ''}`
        const parsedLocation = await getLocationDetailFromAddress(deliveryAddress)
        location.name = `${designerBooked[0]?.name || ''}`
        location.streetAddress = parsedLocation?.streetAddress || ''
        location.address_2 = parsedLocation?.address_2 || ''
        location.city = parsedLocation?.city || ''
        location.state = parsedLocation?.state || ''
        location.zipCode = parsedLocation?.zipCode || ''
        location.fullAddress = rmnl(deliveryAddress)
        location.addressSource = 'DesignerBookedOnAirtable'
      }
      break
    default:
      break
  }

  return location
}

export const getDesignerAddress = (
  designerPortalUser: PortalUserModel[],
  designerBooked: IAirtableDesignerInfo[],
  eventOrderInput?: EventOrder
) => {
  let designerAddress = ''
  switch (true) {
    case !!eventOrderInput?.address_1:
      designerAddress = `${eventOrderInput?.address_1 || ''} ${eventOrderInput?.address_2 || ''}, ${
        eventOrderInput?.city || ''
      }, ${eventOrderInput?.state || ''} ${eventOrderInput?.zip || ''}`
      break

    case !!designerPortalUser[0]?.deliveryAddress_1:
      designerAddress = `${designerPortalUser[0]?.deliveryAddress_1 || ''} ${
        designerPortalUser[0]?.deliveryAddress_2 || ''
      }, ${designerPortalUser[0]?.deliveryCity || ''}, ${designerPortalUser[0]?.deliveryState || ''} ${
        designerPortalUser[0]?.deliveryZip || ''
      }`
      break

    case !!designerPortalUser[0]?.address_1 && designerPortalUser[0]?.isDeliverySameHomeAddress:
      designerAddress = `${designerPortalUser[0]?.address_1 || ''} ${designerPortalUser[0]?.address_2 || ''}, ${
        designerPortalUser[0]?.city || ''
      }, ${designerPortalUser[0]?.state || ''} ${designerPortalUser[0]?.zip || ''}`
      break

    case !!designerBooked[0]?.addressDelivery:
      designerAddress = `${designerBooked[0]?.addressDelivery}`
      break

    case !!designerPortalUser[0]?.address_1:
      designerAddress = `${designerPortalUser[0]?.address_1 || ''} ${designerPortalUser[0]?.address_2 || ''}, ${
        designerPortalUser[0]?.city || ''
      }, ${designerPortalUser[0]?.state || ''} ${designerPortalUser[0]?.zip || ''}`
      break

    default:
      designerAddress = `${designerBooked[0]?.addressHome || ''}`
      break
  }

  return rmnl(designerAddress)
}

export const getDesignerName = (
  designerPortalUser: PortalUserModel[],
  designerBooked: IAirtableDesignerInfo[],
  eventOrderInput?: EventOrder
) => {
  switch (true) {
    case !!eventOrderInput?.recipient:
      return eventOrderInput?.recipient
    case !!designerPortalUser[0]?.firstName:
      return `${designerPortalUser[0]?.firstName} ${designerPortalUser[0]?.lastName || ''}`
    case !!designerBooked[0]?.name:
      return designerBooked[0]?.name
    default:
      return ''
  }
}

export const getFormattedStrAddress = (location: EventLocation) => {
  const strAddress = `${location?.streetAddress || ''}, ${location?.city || ''}, ${location?.state || ''} ${
    location?.zipCode || ''
  }`

  return strAddress
}

export const differenceLocation = (location1: ILocation, location2: ILocation) => {
  return (
    location1.streetAddress !== location2.streetAddress ||
    location1.address_2 !== location2.address_2 ||
    location1.city !== location2.city ||
    location1.state !== location2.state ||
    location1.zipCode !== location2.zipCode
  )
}

const handleDefaultFilter = () => {
  return {
    from: '',
    to: '',
    search: '',
    type: '',
    fmids: '',
    page: 1,
    sortByValue: NotificationSortBy.TASK_DUE_DAY_ASC
  }
}
export const getCurrentPayload = (newData: object = {}) => {
  const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search))
  const defaultPayload = handleDefaultFilter()
  const finalPayload = {
    ...defaultPayload,
    ...currentQuery,
    ...newData
  }

  return finalPayload
}

export const checkUnitTypeFlowerGirlCrown = (product: ReducerProduct) => {
  return product?.unitProduct?.proposalUnit?.unitType === ProposalUnitType.FLOWER_GIRL_CROWN
}
